import { faArrowLeft } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, styled, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { FC, useContext, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { SiteEventType, Species } from '../api/dtos/dtos';
import AreaGraphCard from '../components/datamanagement/chartsandtables/AreaGraphCard';
import ChartsAndTablesCard from '../components/datamanagement/chartsandtables/ChartsAndTablesCard';
import DownloadGroupReport from '../components/datamanagement/chartsandtables/DownloadGroupReport';
import TablesCard from '../components/datamanagement/chartsandtables/TablesCard';
import FilterButton from '../components/map/FilterButton';
import AuthContext from '../contexts/auth/AuthContext';
import MapContext, { MapFilterPeriodEnum, MapFilterSubmitter } from '../contexts/data/MapContext';
import { SpeciesContext } from '../contexts/data/SpeciesContext';
import useGetCustomSiteEventChartData from '../hooks/data/useGetCustomSiteEventChartData';
import useGetSiteEventGetCollection from '../hooks/data/useGetSiteEventGetCollection';

const ContentWrapper = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default,
}));

const ChartsAndTablesContent: FC = () => {
    //
    // -- Contexts --
    const { user } = useContext(AuthContext);
    const { filter, closeDrawer } = useContext(MapContext);
    const { species: {species} } = useContext(SpeciesContext);
    
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Params --
    const { species: speciesName } = useParams();

    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]);

    const isDataAccessForThisSpecies = useMemo(() => {
        return user?.matchingRegionAdminRoles != undefined && user?.matchingRegionAdminRoles?.length > 0;
    }, [user]);

    // Cleanup
    useEffect(() => {
        return () => {
            closeDrawer();
        }
    }, []);

    // Generate the chart and table data request based off map filter values
    const chartDataRequest = useMemo(() => {

        // Period
        let from = null;
        let to = null;
        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange: 
                from = filter.reportingPeriod.fromField;
                to = filter.reportingPeriod.toField;
                break;

            case MapFilterPeriodEnum.Year: 
                const year = filter.reportingPeriod.yearField;
                from = year?.startOf('year');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (year?.year() === dayjs().year()) {
                    to = dayjs();
                } else {
                    to = year?.endOf('year');
                }

                break;
                
            case MapFilterPeriodEnum.MonthYear: 
                const monthYear = filter.reportingPeriod.monthYearField;
                from = monthYear?.startOf('month');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                    to = dayjs();
                } else {
                    to = monthYear?.endOf('month');
                }

                break;

            case MapFilterPeriodEnum.LastSevenDays: 
                from = dayjs().subtract(7, 'days');  
                to = dayjs();  
                break;

            case MapFilterPeriodEnum.LastThirtyOneDays: 
                from = dayjs().subtract(31, 'days');  
                to = dayjs();  
                break;
        }

        switch (filter.bySubmitter.submitter) {
            case MapFilterSubmitter.AllData:
                return {
                    species: species, 
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY'),
                    getChartAndTableData: true,
                    locatedInRegionIds: (!isAdminOrSuperAdmin && isDataAccessForThisSpecies) ? filter.bySubmitter.locatedInRegionIds : undefined,
                    dataAccessId: (!isAdminOrSuperAdmin && isDataAccessForThisSpecies) ? filter.bySubmitter.dataAccessId : undefined,
                };
            case MapFilterSubmitter.PublicData:
                return {
                    species: species,
                    onlyPublicData: true,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY'),
                    getChartAndTableData: true,
                };
            case MapFilterSubmitter.MyData:
                return {
                    species: species,
                    userId: user?.id,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY'),
                    getChartAndTableData: true,
                };
            case MapFilterSubmitter.MyGroup:
                if (!filter.bySubmitter.communityGroupId) {
                    return undefined;
                }
                return {
                    species: species,
                    userId: filter.bySubmitter.communityGroupMemberId != -1 ? filter.bySubmitter.communityGroupMemberId : undefined,
                    memberOfCommunityGroupIds: filter.bySubmitter.communityGroupId ? [filter.bySubmitter.communityGroupId] : undefined,
                    startDateOnOrAfter: from?.format('DD/MM/YYYY'),
                    startDateOnOrBefore: to?.format('DD/MM/YYYY'),
                    getChartAndTableData: true,
                };
        }
    }, [user, filter, species]);

    // Chart data requests 
    const { data: sightingChartData } = useGetSiteEventGetCollection(
        (filter.recordType.sighting) ? 
            {
                ...chartDataRequest,
                siteEventType: SiteEventType.Sighting,
            } :
            undefined,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    ) ;

    const { data: damageChartData } = useGetSiteEventGetCollection(
        (filter.recordType.damage) ?
            {
                ...chartDataRequest,
                siteEventType: SiteEventType.Damage,
            } :
            undefined,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    const { data: controlChartData } = useGetSiteEventGetCollection(
        (filter.recordType.control) ?
            {
                ...chartDataRequest,
                siteEventType: SiteEventType.Control,
            } :
            undefined,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    ) ;

    const { data: wildDogCustomData } =  useGetCustomSiteEventChartData(
        species === Species.WildDog ? 
            {
                ...chartDataRequest
            } :
            undefined,
        { revalidateOnFocus: false, errorRetryCount: 0 }
    );

    return (
        <Box>
            <ContentWrapper>
                {/* Header */}
                <Typography fontSize={32} color={"#FFFFFF"} sx={{paddingTop: "15px"}}>Charts And Tables</Typography> 
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        color: 'white',
                        gap: '8px'
                    }}
                >
                    <Button
                        variant="text"
                        sx={{
                            color: 'white',
                            fontSize: '0.65rem',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.15)'
                            }
                        }}
                        startIcon={
                            <FontAwesomeIcon
                                icon={faArrowLeft} 
                                style={{ 
                                    color: 'white',
                                    fontSize: '0.65rem',
                                    paddingRight: '3px' 
                                }}
                            />
                        }
                        onClick={() => navigate("/" + speciesName + "/datamanagement")}
                    >
                        Back to Data Management
                    </Button>
                    {
                        filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup && (
                            <DownloadGroupReport 
                                activeGroupMembers={sightingChartData?.activeGroupMembers?.length} 
                                groupChartMemberNames={sightingChartData?.groupChartMemberNames}
                            />
                        )
                    }
                </Box>

                {/* Map Filter */}
                <Box style={{position: 'fixed', right: "0", padding: '16px', zIndex: 1}}>
                    <FilterButton 
                        loading={false}
                        disabled={false}
                    />
                </Box>

                {((filter.recordType.sighting && sightingChartData?.chartDataByDate && sightingChartData?.chartDataByDate?.length > 0) ||
                (filter.recordType.damage && damageChartData?.chartDataByDate && damageChartData?.chartDataByDate?.length > 0)) && (
                    <AreaGraphCard 
                        title={filter.recordType.sighting ? filter.recordType.damage ? "Sighting and Damage" : "Sighting" : filter.recordType.damage ? "Damage" : ""}
                        siteeventtypes={[SiteEventType.Sighting, SiteEventType.Damage]}
                        sightingChartData={sightingChartData?.chartDataByDate}
                        damageChartData={damageChartData?.chartDataByDate}
                        wildDogCustomData={wildDogCustomData?.chartDataByDate}
                    />
                )}

                {(filter.recordType.control && controlChartData?.chartDataByDate && controlChartData?.chartDataByDate?.length > 0) && (
                    <AreaGraphCard 
                        title={"Control"}
                        siteeventtypes={[SiteEventType.Control]}
                        controlChartData={controlChartData?.chartDataByDate}
                    />
                )}

                {
                    filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup ?
                        <ChartsAndTablesCard
                            sightingTableData={sightingChartData?.tableData}
                            damageTableData={damageChartData?.tableData}
                            controlTableData={controlChartData?.tableData}
                            sightingGroupChartDataSet={sightingChartData?.groupChartDataSets}
                            damageGroupChartDataSet={damageChartData?.groupChartDataSets}
                            controlGroupChartDataSet={controlChartData?.groupChartDataSets}
                            sightingGroupChartLabels={sightingChartData?.groupChartLabels}
                            damageGroupChartLabels={damageChartData?.groupChartLabels}
                            controlGroupChartLabels={controlChartData?.groupChartLabels}
                        /> :
                        <TablesCard
                            sightingTableData={sightingChartData?.tableData}
                            damageTableData={damageChartData?.tableData}
                            controlTableData={controlChartData?.tableData}
                        />
                }
                
            </ContentWrapper>
        </Box>
    )
}

const ChartsAndTablesPage: FC = () => {
    const context = useContext(AuthContext);
    const { species: speciesName } = useParams();

    if (!context.user) {
        return <Navigate to={"/" + speciesName + "/map"} replace />
    }

    return (<ChartsAndTablesContent />)
}

export default ChartsAndTablesPage
